import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCurrentShop, getShopOwnerCurrencySync } from "@/lib/auth";
import {
  getShopDashboardStats,
  getShopInventory,
  getProducts,
  getShopProfitByMethod,
} from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";

// Iconos de lucide-react - NOMBRES ÚNICOS
import {
  BarChart as BarChartIcon,
  DollarSign,
  Package,
  AlertTriangle,
  Loader2,
  TrendingUp,
  Users,
  ShoppingBag,
  Clock,
  PieChart as PieChartIcon,
  BarChart3 as BarChart3Icon,
  ArrowUp,
  ArrowDown,
  Calendar as CalendarIcon,
} from "lucide-react";

// Gráficos de recharts
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  getTodayRange,
  getStartOfWeek,
  getEndOfWeek,
  getStartOfMonth,
  getEndOfMonth,
  formatLocalDate,
  diagnoseSystem,
} from "@/lib/utils/date-utils";
import { usePlan } from "@/hooks/usePlan";

// Tipo para items de inventario (basado en los datos que vimos en la API)
type InventoryItem = {
  id: string;
  shopId: string;
  productId: string;
  supplierId: string | null;
  price: string;
  stock: number;
  lowStockThreshold: number;
  supplierName: string | null;
  supplierContact: string | null;
  supplierNotes: string | null;
  acquisitionCost: string | null;
  profitMargin: string | null;
  suggestedPrice: string | null;
  batchNumber: string | null;
  purchaseDate: string | null;
  expiryDate: string | null;
  manufactureDate: string | null;
  minimumOrderQuantity: number;
  leadTimeDays: number;
  reorderPoint: number | null;
  dailySalesRate: string;
  updatedAt: string;
  supplier: any | null;
};

// Función para parsear fecha desde string de base de datos manteniendo hora exacta
const parseDatabaseDate = (dateString: string): Date => {
  // 1. INTENTAR: Formato ISO con Z (UTC) - "2025-12-23T14:48:29.527Z"
  const isoMatch = dateString.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/,
  );

  if (isoMatch) {
    const [_, year, month, day, hours, minutes, seconds] = isoMatch;

    // Crear fecha local PERO usando las horas UTC como si fueran locales
    // Esto hace que 14:48 UTC se muestre como 14:48 hora local
    const localDateWithUtcHours = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours), // Usar horas UTC como horas locales
      parseInt(minutes), // Usar minutos UTC como minutos locales
      parseInt(seconds), // Usar segundos UTC como segundos locales
    );

    return localDateWithUtcHours;
  }

  // 2. INTENTAR: Formato SQL normal - "2025-12-22 19:57:28.561964"
  const sqlMatch = dateString.match(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/,
  );

  if (sqlMatch) {
    const [_, year, month, day, hours, minutes, seconds] = sqlMatch;

    // Crear fecha local (asumiendo que ya es hora local)
    const localDate = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes),
      parseInt(seconds),
    );

    return localDate;
  }

  // 3. ÚLTIMO RECURSO: Usar new Date()
  return new Date(dateString);
};

// Función para formatear fecha con hora local exacta
const formatDateTimeLocal = (dateInput: string | Date): string => {
  // Convertir a string si es Date
  const dateString =
    dateInput instanceof Date ? dateInput.toISOString() : dateInput;

  const date = parseDatabaseDate(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  const result = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;

  return result;
};

// Tipos para TypeScript
type PaymentMethod = "efectivo" | "transferencia";
type Sale = {
  saleId: string;
  createdAt: string;
  total: number;
  paymentMethod: PaymentMethod;
  customerName?: string;
  productName?: string; // ← NUEVO
};

type CustomerWithStats = {
  id: string;
  fullName: string;
  phone: string;
  totalPurchases: number;
  totalSpent: number;
  lastPurchaseAt?: string;
  daysSinceLastPurchase: number;
};

type ProductSale = {
  productId: string;
  productName: string;
  totalQuantity: number;
  totalRevenue: number;
  saleCount: number;
  category: string;
};

// Función para obtener símbolo de moneda
const getCurrencySymbol = (): string => {
  const currency = getShopOwnerCurrencySync();
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    CUP: "$",
    L: "L",
    MXN: "$",
  };
  return symbols[currency] || "$";
};

export default function ShopDashboard() {
  const currentShop = getCurrentShop();

  // Obtener información del plan del owner
  const { can, loading: planLoading } = usePlan();

  // Obtener información del owner desde localStorage
  const [ownerInfo, setOwnerInfo] = useState<any>(null);

  useEffect(() => {
    const ownerStr = localStorage.getItem("currentOwner");
    if (ownerStr) {
      try {
        const owner = JSON.parse(ownerStr);
        setOwnerInfo(owner);
      } catch (e) {
        console.error("Error parsing owner:", e);
      }
    }
  }, []);

  // ========== ESTADO PARA FECHA ACTUAL ==========
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // {Agrega este debug de hora del sistema}
  useEffect(() => {
    const ahora = new Date();
    console.log("🕐 [DEBUG Sistema] Información de hora:", {
      fechaCompleta: ahora.toString(),
      horaLocal: ahora.getHours(),
      minutosLocal: ahora.getMinutes(),
      horaUTC: ahora.getUTCHours(),
      minutosUTC: ahora.getUTCMinutes(),
      diferenciaHoras: ahora.getHours() - ahora.getUTCHours(),
      zonaHoraria: Intl.DateTimeFormat().resolvedOptions().timeZone,
      esHorarioVerano: ahora.getTimezoneOffset() < 0 ? "Sí" : "No",
    });
  }, []);

  // Efecto para actualizar la fecha cada minuto
  useEffect(() => {
    const updateDate = () => {
      const now = new Date();
      setCurrentDate(now);
      console.log("🔄 Fecha actualizada:", now.toISOString());
    };

    // Actualizar inmediatamente
    updateDate();

    // Actualizar cada minuto
    const interval = setInterval(updateDate, 60000);

    // Actualizar cuando la ventana gana foco
    window.addEventListener("focus", updateDate);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", updateDate);
    };
  }, []);

  // Configurar locale español para date-fns
  const esLocale = {
    code: "es",
    formatDistance: () => "",
    formatRelative: () => "",
    localize: {
      ordinalNumber: () => "",
      era: () => "",
      quarter: () => "",
      month: (n: number) =>
        [
          "Enero",
          "Febrero",
          "Marzo",
          "Abril",
          "Mayo",
          "Junio",
          "Julio",
          "Agosto",
          "Septiembre",
          "Octubre",
          "Noviembre",
          "Diciembre",
        ][n],
      day: (n: number) =>
        [
          "Domingo",
          "Lunes",
          "Martes",
          "Miércoles",
          "Jueves",
          "Viernes",
          "Sábado",
        ][n],
      dayPeriod: () => "",
    },
    formatLong: {
      date: () => "",
      time: () => "",
      dateTime: () => "",
    },
    match: {
      ordinalNumber: () => ({}) as any,
      era: () => ({}) as any,
      quarter: () => ({}) as any,
      month: () => ({}) as any,
      day: () => ({}) as any,
      dayPeriod: () => ({}) as any,
    },
    options: {
      weekStartsOn: 0,
      firstWeekContainsDate: 1,
    },
  };

  // Estado para el rango de fechas
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().setHours(0, 0, 0, 0)), // Hoy a las 00:00
    to: new Date(new Date().setHours(23, 59, 59, 999)), // Hoy a las 23:59
  });

  // Estado para modo rápido
  const [quickRange, setQuickRange] = useState<
    "today" | "week" | "month" | "custom"
  >("today");

  // ========== FUNCIÓN "HOY" SIMPLIFICADA ==========
  const getTodayRangeFixed = (): { from: Date; to: Date } => {
    // Rango de HOY en hora Cuba
    const { start, end } = getTodayRange();

    console.log("🎯 Rango HOY Cuba:", {
      inicio: start.toLocaleString("es-CU"),
      fin: end.toLocaleString("es-CU"),
    });

    return { from: start, to: end };
  };

  // Función para formatear fecha en hora LOCAL (lo que el usuario ve)
  const formatLocalDate = (date: Date): string => {
    // Crear una copia local de la fecha
    const localDate = new Date(date.getTime());

    // Obtener componentes locales
    const day = localDate.getDate();
    const month = localDate.getMonth() + 1;
    const year = localDate.getFullYear();

    return `${day.toString().padStart(2, "0")}-${month.toString().padStart(2, "0")}-${year}`;
  };

  // DEBUG DETALLADO: Ver cada cambio en dateRange
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      // Calcular diferencia exacta
      const diffMs = dateRange.to.getTime() - dateRange.from.getTime();
      const diffHoras = diffMs / (1000 * 60 * 60);
      const diffDias = diffHoras / 24;

      // Detectar si es rango incorrecto (más de 1.5 días)
      if (diffDias > 1.5) {
      }

      // Verificar si es "Hoy" correcto (rango de ~24 horas)
      const esHoy = diffHoras >= 23.9 && diffHoras <= 24.1;
      if (esHoy) {
      }
    } else {
    }
  }, [dateRange]);

  // Consulta principal: Dashboard stats desde el backend
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: [
      "shop-dashboard-stats",
      currentShop?.id,
      dateRange?.from,
      dateRange?.to,
    ],
    queryFn: () =>
      getShopDashboardStats(currentShop!.id, {
        startDate: dateRange?.from ? dateRange.from.toISOString() : undefined,
        endDate: dateRange?.to ? dateRange.to.toISOString() : undefined,
      }),
    enabled: !!currentShop,
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
  });

  // Consulta para ganancia por método de pago
  const { data: profitData, isLoading: profitLoading } = useQuery({
    queryKey: [
      "shop-profit-by-method",
      currentShop?.id,
      dateRange?.from,
      dateRange?.to,
    ],
    queryFn: () =>
      getShopProfitByMethod(currentShop!.id, {
        startDate: dateRange?.from ? dateRange.from.toISOString() : undefined,
        endDate: dateRange?.to ? dateRange.to.toISOString() : undefined,
      }),
    enabled: !!currentShop,
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
  });

  // Solo necesitamos inventario y productos (para alertas de stock)
  const {
    data: inventory = [] as InventoryItem[],
    isLoading: inventoryLoading,
    error: inventoryError,
  } = useQuery({
    queryKey: ["shopInventory", currentShop?.id],
    queryFn: async () => {
      if (!currentShop?.id) return [];
      try {
        const result = await getShopInventory(currentShop.id);
        return result || [];
      } catch (error) {
        console.error("Error al obtener inventario:", error);
        return [];
      }
    },
    enabled: !!currentShop,
  });

  // Estado para lowStockItems
  const [lowStockItems, setLowStockItems] = useState<InventoryItem[]>([]);

  // {esta sección agrega estados para la paginación de las tarjetas del dashboard}
  const [salesPage, setSalesPage] = useState(1);
  const [alertsPage, setAlertsPage] = useState(1);
  const ITEMS_PER_PAGE = 10; // Límite fijo por página

  // Efecto para recalcular cuando inventory cambie
  useEffect(() => {
    const filtered = inventory.filter((i) => {
      const stock = Number(i.stock) || 0;
      const threshold = Number(i.lowStockThreshold) || 0;
      return stock <= threshold;
    });
    setLowStockItems(filtered);
  }, [inventory]);

  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });

  if (!currentShop) return null;

  // {esta sección define el fallback para paymentMethods incluyendo el nuevo campo mixto}
  const stats = dashboardStats?.data || {
    summary: {
      totalRevenue: 0,
      totalSales: 0,
      averageTicket: 0,
      todayRevenue: 0,
      todaySales: 0,
      todayAverageTicket: 0,
      totalProducts: inventory.length,
      lowStockItems: lowStockItems.length,
      uniqueCustomersToday: 0,
      activeCustomers: 0,
    },
    paymentMethods: {
      efectivo: 0,
      transferencia: 0,
      mixto: 0, // ← AGREGAR ESTA LÍNEA
    },
    hourlySales: [],
    topProducts: [],
    recentSales: [],
    recentCustomers: [],
  };

  // {esta sección calcula los datos paginados para las tarjetas del dashboard}
  // Paginación para Últimas Ventas
  const paginatedSales = stats.recentSales.slice(
    (salesPage - 1) * ITEMS_PER_PAGE,
    salesPage * ITEMS_PER_PAGE,
  );
  const salesTotalPages = Math.ceil(stats.recentSales.length / ITEMS_PER_PAGE);

  // Paginación para Alertas de Inventario
  const paginatedAlerts = lowStockItems.slice(
    (alertsPage - 1) * ITEMS_PER_PAGE,
    alertsPage * ITEMS_PER_PAGE,
  );
  const alertsTotalPages = Math.ceil(lowStockItems.length / ITEMS_PER_PAGE);

  // Determinar si es un solo día (para gráfico de horas)
  const isSingleDay =
    dateRange?.from &&
    dateRange?.to &&
    dateRange.from.toDateString() === dateRange.to.toDateString();

  // Ventas por hora (formatear datos del backend) - CON DEBUG
  const salesByHour =
    stats.hourlySales?.map((hour) => {
      return {
        hour: `${hour.hour}:00`,
        ventas: hour.salesCount,
        ingresos: hour.revenue,
      };
    }) || [];

  // {Agrega también un console.log para ver todas las horas juntas}
  if (stats.hourlySales && stats.hourlySales.length > 0) {
    console.log("📊 [DEBUG Frontend] Resumen de horas recibidas:", {
      totalHorasConDatos: stats.hourlySales.filter((h) => h.salesCount > 0)
        .length,
      horasConVentas: stats.hourlySales
        .filter((h) => h.salesCount > 0)
        .map((h) => ({
          hora: h.hour,
          ventas: h.salesCount,
          ingresos: h.revenue,
        })),
      horaActualSistema: new Date().getHours(),
      horaActualSistemaUTC: new Date().getUTCHours(),
    });
  }

  // Top productos (formatear datos del backend)
  const topProducts =
    stats.topProducts?.slice(0, 5).map((product) => ({
      name:
        product.productName.length > 15
          ? product.productName.substring(0, 15) + "..."
          : product.productName,
      cantidad: product.totalQuantity,
      ingresos: product.totalRevenue,
    })) || [];

  // {esta sección incluye pago mixto en el gráfico de distribución de métodos de pago}
  const pieChartData = [
    {
      name: "Efectivo",
      value: stats.paymentMethods.efectivo || 0,
      color: "#10B981", // Verde
    },
    {
      name: "Transferencia",
      value: stats.paymentMethods.transferencia || 0,
      color: "#3B82F6", // Azul
    },
    {
      name: "Mixto",
      value: stats.paymentMethods.mixto || 0,
      color: "#8B5CF6", // Púrpura (distintivo para mixto)
    },
  ];

  // {esta sección filtra solo métodos con valor > 0 para el gráfico}
  const filteredPieData = pieChartData.filter((item) => item.value > 0);

  // Clientes recientes
  const recentCustomers = stats.recentCustomers || [];

  // Actualizar Loading state
  const isLoading = statsLoading || inventoryLoading;

  // Si el owner está suspendido, mostrar mensaje
  if (ownerInfo?.accountStatus === "suspended") {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-2xl">🔴</span>
              <h1 className="text-2xl font-bold text-red-700">
                Cuenta Suspendida
              </h1>
            </div>
            <p className="text-red-600 mb-4">
              La cuenta del propietario ha sido suspendida. No puedes acceder a
              las funcionalidades del sistema.
            </p>
          </div>

          <div className="max-w-md p-6 border rounded-lg bg-gray-50">
            <h2 className="text-lg font-semibold mb-3">¿Qué puedes hacer?</h2>
            <ul className="text-left space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">•</span>
                <span>Contacta al propietario para regularizar el pago</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">•</span>
                <span>Verifica el estado de la suscripción</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">•</span>
                <span>Comunícate con el administrador del sistema</span>
              </li>
            </ul>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-medium text-blue-800 mb-2">
                Información de contacto:
              </h3>
              <p className="text-sm text-blue-600">
                Email: fm.izquierdo@nauta.cu
                <br />
                Teléfono: +53 54882577
                <br />
                Horario: Lunes a Domingo, 9:00 AM - 5:00 PM
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageContainer className="container mx-auto px-4 py-8">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase text-primary">
            Dashboard Avanzado
            {dateRange?.from && dateRange?.to && (
              <span
                className="text-lg font-normal normal-case block sm:inline sm:ml-4 text-muted-foreground"
                title={`UTC: ${dateRange.from.toISOString()} a ${dateRange.to.toISOString()}`}
              >
                {formatLocalDate(dateRange.from)} -{" "}
                {formatLocalDate(dateRange.to)}
                <span
                  className="text-xs ml-2 text-blue-500"
                  title="Mostrando fechas UTC"
                >
                  (UTC)
                </span>
              </span>
            )}
          </h1>
          <p className="text-muted-foreground">
            Bienvenido de nuevo, {currentShop.owner}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {/* Indicador de estado de suscripción */}
          <div className="mt-2">
            {(() => {
              if (!ownerInfo) {
                return (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-gray-700">
                      Cargando información...
                    </span>
                  </div>
                );
              }

              const ownerStatus = ownerInfo.accountStatus;
              const isTrial = ownerStatus === "trial";
              const isActive = ownerStatus === "active";
              const isSuspended = ownerStatus === "suspended";

              // Determinar fecha de vencimiento correcta
              let endDate = null;
              let daysRemaining = 0;

              if (isTrial) {
                endDate = ownerInfo.trialEndDate;
              } else if (isActive) {
                endDate =
                  ownerInfo.subscriptionEndDate || ownerInfo.nextPaymentDate;
              }

              // Calcular días restantes si hay fecha
              if (endDate) {
                const endDateObj = new Date(endDate);
                const today = new Date();

                // Ajustar ambas fechas a medianoche
                endDateObj.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);

                const timeDiff = endDateObj.getTime() - today.getTime();
                daysRemaining = Math.max(
                  0,
                  Math.floor(timeDiff / (1000 * 60 * 60 * 24)),
                );
              }

              // Estado: Suspendido
              if (isSuspended) {
                return (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-lg">🔴</span>
                    <span className="text-sm font-medium text-red-700">
                      Cuenta suspendida - Contactar soporte
                    </span>
                  </div>
                );
              }

              // Estado: Trial
              if (isTrial) {
                if (!ownerInfo.trialEndDate) {
                  return (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                      <span className="text-lg">⏳</span>
                      <span className="text-sm font-medium text-blue-700">
                        Período de prueba activo
                      </span>
                    </div>
                  );
                }

                if (daysRemaining > 0) {
                  return (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                      <span className="text-lg">⏳</span>
                      <span className="text-sm font-medium text-blue-700">
                        Prueba: {daysRemaining} día
                        {daysRemaining !== 1 ? "s" : ""} restante
                        {daysRemaining !== 1 ? "s" : ""}
                      </span>
                    </div>
                  );
                } else if (daysRemaining === 0) {
                  return (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-lg">⏳</span>
                      <span className="text-sm font-medium text-amber-700">
                        Prueba: Último día
                      </span>
                    </div>
                  );
                } else {
                  return (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-lg">⚠️</span>
                      <span className="text-sm font-medium text-amber-700">
                        Período de prueba expirado - Actualizar suscripción
                      </span>
                    </div>
                  );
                }
              }

              // Estado: Activo
              if (isActive && endDate) {
                if (daysRemaining <= 5 && daysRemaining > 0) {
                  return (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-lg">⚠️</span>
                      <span className="text-sm font-medium text-amber-700">
                        Por vencer ({daysRemaining} día
                        {daysRemaining !== 1 ? "s" : ""} restante
                        {daysRemaining !== 1 ? "s" : ""})
                      </span>
                    </div>
                  );
                }

                return (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-lg">✅</span>
                    <span className="text-sm font-medium text-green-700">
                      Activo (Vence: {format(new Date(endDate), "dd/MM/yyyy")})
                    </span>
                  </div>
                );
              }

              // Estado por defecto
              return (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                  <span className="text-lg">ℹ️</span>
                  <span className="text-sm font-medium text-gray-700">
                    Estado: {ownerStatus || "No disponible"}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Selector rápido */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={quickRange === "today" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                const { from, to } = getTodayRangeFixed();
                setDateRange({ from, to });
                setQuickRange("today");
              }}
            >
              Hoy
            </Button>
            <Button
              variant={quickRange === "week" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                const freshNow = new Date();
                setCurrentDate(freshNow);

                // USAR LAS NUEVAS FUNCIONES (hora Cuba)
                const startOfWeek = getStartOfWeek(freshNow); // Lunes 00:00 hora Cuba
                const endOfWeek = getEndOfWeek(freshNow); // Domingo 23:59 hora Cuba

                console.log("[DEBUG] Esta semana calculada (hora Cuba):", {
                  startOfWeek: startOfWeek.toISOString(),
                  endOfWeek: endOfWeek.toISOString(),
                  "Inicio (Cuba)": startOfWeek.toLocaleString("es-CU"),
                  "Fin (Cuba)": endOfWeek.toLocaleString("es-CU"),
                  hoy: freshNow.toLocaleString("es-CU"),
                });

                setDateRange({ from: startOfWeek, to: endOfWeek });
                setQuickRange("week");
              }}
            >
              Esta semana
            </Button>
            <Button
              variant={quickRange === "month" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                const freshNow = new Date();
                setCurrentDate(freshNow);

                // USAR LAS NUEVAS FUNCIONES (hora Cuba)
                const startOfMonth = getStartOfMonth(freshNow); // Día 1, 00:00 hora Cuba
                const endOfMonth = getEndOfMonth(freshNow); // Último día, 23:59 hora Cuba

                console.log("[DEBUG] Este mes calculado (hora Cuba):", {
                  startOfMonth: startOfMonth.toISOString(),
                  endOfMonth: endOfMonth.toISOString(),
                  "Inicio (Cuba)": startOfMonth.toLocaleString("es-CU"),
                  "Fin (Cuba)": endOfMonth.toLocaleString("es-CU"),
                });

                setDateRange({ from: startOfMonth, to: endOfMonth });
                setQuickRange("month");
              }}
            >
              Este mes
            </Button>
          </div>

          {/* Selectores de fecha independientes */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* Fecha de inicio */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">
                Desde:
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[160px] justify-start text-left font-normal",
                      !dateRange?.from && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange?.from ? (
                      format(dateRange.from, "dd/MM/yyyy")
                    ) : (
                      <span>Selecciona fecha</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateRange?.from}
                    onSelect={(date) => {
                      if (date) {
                        // El usuario selecciona "13 Enero" - guardar como ese día en hora Cuba
                        const localDate = new Date(date);

                        // Para inicio: 00:00 hora Cuba
                        localDate.setHours(0, 0, 0, 0);

                        // Para fin: 23:59 hora Cuba
                        localDate.setHours(23, 59, 59, 999);

                        console.log(
                          "📅 Fecha seleccionada (hora Cuba):",
                          localDate.toLocaleString("es-CU"),
                        );

                        // Guardar directamente
                        const newDateRange = {
                          from: localDate,
                          to: dateRange?.to || localDate,
                        };
                        setDateRange(newDateRange);
                        setQuickRange("custom");
                      }
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Fecha de fin */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">
                Hasta:
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[160px] justify-start text-left font-normal",
                      !dateRange?.to && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange?.to ? (
                      format(dateRange.to, "dd/MM/yyyy")
                    ) : (
                      <span>Selecciona fecha</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateRange?.to}
                    onSelect={(date) => {
                      if (date) {
                        // El usuario selecciona "13 Enero" - guardar como ese día LOCAL a las 23:59
                        const localDate = new Date(date);
                        localDate.setHours(23, 59, 59, 999);

                        console.log('📅 CALENDARIO "HASTA" seleccionado:');
                        console.log(
                          "   - Lo que el usuario ve:",
                          localDate.toString(),
                        );
                        console.log("   - Día local:", localDate.getDate());

                        // Para backend: convertir a UTC manteniendo el "día" que el usuario ve
                        const utcDate = new Date(
                          Date.UTC(
                            localDate.getFullYear(),
                            localDate.getMonth(),
                            localDate.getDate(),
                            23,
                            59,
                            59,
                            999,
                          ),
                        );

                        console.log(
                          "   - Para backend (UTC):",
                          utcDate.toISOString(),
                        );

                        const newDateRange = {
                          from: dateRange?.from || utcDate,
                          to: utcDate,
                        };
                        setDateRange(newDateRange);
                        setQuickRange("custom");
                      }
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Mostrar total de días (basado en UTC) */}
            {dateRange?.from && dateRange?.to && (
              <div
                className="text-sm font-medium bg-primary/10 text-primary px-3 py-1.5 rounded-md"
                title={`Días UTC: ${dateRange.from.getUTCDate()} a ${dateRange.to.getUTCDate()}`}
              >
                {Math.ceil(
                  (dateRange.to.getTime() - dateRange.from.getTime()) /
                    (1000 * 60 * 60 * 24) +
                    1,
                )}{" "}
                días
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* KPIs PRINCIPALES - 6 tarjetas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
            {/* Ventas Hoy */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">Ventas Hoy</p>
                    <p
                      className="text-2xl font-bold mt-2"
                      data-testid="text-today-revenue"
                    >
                      {getCurrencySymbol()}
                      {stats.summary.todayRevenue.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      {stats.summary.todayRevenue > 0 ? (
                        <>
                          <ArrowUp className="h-3 w-3 text-green-500" />
                          <span className="text-xs text-green-500">
                            {stats.summary.todaySales} transacciones
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Sin ventas hoy
                        </span>
                      )}
                    </div>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500 bg-green-50 p-1.5 rounded-lg" />
                </div>
              </CardContent>
            </Card>

            {/* Transacciones Hoy */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Transacciones Hoy
                    </p>
                    <p className="text-2xl font-bold mt-2">
                      {stats.summary.todaySales}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Ticket: {getCurrencySymbol()}
                      {stats.summary.todayAverageTicket.toFixed(2)}
                    </p>
                  </div>
                  <ShoppingBag className="h-8 w-8 text-blue-500 bg-blue-50 p-1.5 rounded-lg" />
                </div>
              </CardContent>
            </Card>

            {/* Clientes Atendidos */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Clientes Hoy
                    </p>
                    <p className="text-2xl font-bold mt-2">
                      {stats.summary.uniqueCustomersToday}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {stats.summary.activeCustomers} activos
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-purple-500 bg-purple-50 p-1.5 rounded-lg" />
                </div>
              </CardContent>
            </Card>

            {/* Productos Vendidos Hoy */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Productos Vendidos
                    </p>
                    <p className="text-2xl font-bold mt-2">
                      {stats.topProducts.reduce(
                        (sum, p) => sum + p.totalQuantity,
                        0,
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {stats.topProducts.length} productos distintos
                    </p>
                  </div>
                  <Package className="h-8 w-8 text-amber-500 bg-amber-50 p-1.5 rounded-lg" />
                </div>
              </CardContent>
            </Card>

            {/* Ticket Promedio */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Ticket Promedio
                    </p>
                    <p className="text-2xl font-bold mt-2">
                      {getCurrencySymbol()}
                      {stats.summary.averageTicket.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      {stats.summary.averageTicket > 0 ? (
                        <>
                          <TrendingUp className="h-3 w-3 text-green-500" />
                          <span className="text-xs text-green-500">
                            {stats.summary.totalSales > 0
                              ? `${stats.summary.totalSales} ventas`
                              : "Sin ventas"}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Sin ventas en el período
                        </span>
                      )}
                    </div>
                  </div>
                  <BarChartIcon className="h-8 w-8 text-rose-500 bg-rose-50 p-1.5 rounded-lg" />
                </div>
              </CardContent>
            </Card>

            {/* Alertas Stock */}
            <Card
              className={
                lowStockItems.length > 0
                  ? "border-destructive/50 bg-destructive/5"
                  : ""
              }
            >
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Alertas Stock
                    </p>
                    <p
                      className={`text-2xl font-bold mt-2 ${lowStockItems.length > 0 ? "text-destructive" : ""}`}
                    >
                      {lowStockItems.length}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {lowStockItems.length > 0
                        ? "Necesita atención"
                        : "Todo en orden"}
                    </p>
                  </div>
                  <AlertTriangle
                    className={`h-8 w-8 p-1.5 rounded-lg ${lowStockItems.length > 0 ? "text-destructive bg-destructive/10" : "text-gray-400 bg-gray-50"}`}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Ganancia Limpia Efectivo */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Ganancia Efectivo
                    </p>
                    <p
                      className="text-2xl font-bold mt-2 text-emerald-600"
                      data-testid="text-profit-cash"
                    >
                      {getCurrencySymbol()}
                      {profitData?.data?.efectivo?.ganancia?.toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      ) || "0.00"}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-xs text-muted-foreground">
                        Margen:{" "}
                        {profitData?.data?.efectivo?.margen?.toFixed(1) || 0}%
                      </span>
                      <span className="text-xs text-muted-foreground">
                        (Ventas: {getCurrencySymbol()}
                        {profitData?.data?.efectivo?.totalVentas?.toLocaleString() ||
                          0}
                        )
                      </span>
                    </div>
                  </div>
                  <DollarSign className="h-8 w-8 text-emerald-500 bg-emerald-50 p-1.5 rounded-lg" />
                </div>
              </CardContent>
            </Card>

            {/* Ganancia Limpia Transferencia */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Ganancia Transferencia
                    </p>
                    <p
                      className="text-2xl font-bold mt-2 text-blue-600"
                      data-testid="text-profit-transfer"
                    >
                      {getCurrencySymbol()}
                      {profitData?.data?.transferencia?.ganancia?.toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      ) || "0.00"}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-xs text-muted-foreground">
                        Margen:{" "}
                        {profitData?.data?.transferencia?.margen?.toFixed(1) ||
                          0}
                        %
                      </span>
                      <span className="text-xs text-muted-foreground">
                        (Ventas: {getCurrencySymbol()}
                        {profitData?.data?.transferencia?.totalVentas?.toLocaleString() ||
                          0}
                        )
                      </span>
                    </div>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-500 bg-blue-50 p-1.5 rounded-lg" />
                </div>
              </CardContent>
            </Card>
            {/* Ganancia Total del Día */}
            <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-semibold text-purple-700">
                      💰 Ganancia Total
                    </p>
                    <p
                      className="text-3xl font-bold mt-2 text-purple-700"
                      data-testid="text-profit-total"
                    >
                      {getCurrencySymbol()}
                      {profitData?.data?.total?.ganancia?.toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      ) || "0.00"}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-purple-600 font-medium">
                        Margen:{" "}
                        {profitData?.data?.total?.margen?.toFixed(1) || 0}%
                      </span>
                      <span className="text-xs text-purple-500">
                        (Ventas: {getCurrencySymbol()}
                        {profitData?.data?.total?.totalVentas?.toLocaleString() ||
                          0}
                        )
                      </span>
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* GRÁFICOS PRINCIPALES - 2 columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Gráfico de ventas por hora */}
            <Card className="h-[400px] flex flex-col">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  {isSingleDay
                    ? `Ventas por Hora (${dateRange?.from ? format(dateRange.from, "dd/MM/yyyy") : "Hoy"})`
                    : `Resumen del Período (${dateRange?.from ? format(dateRange.from, "dd/MM/yyyy") : ""}${dateRange?.to ? ` - ${format(dateRange.to, "dd/MM/yyyy")}` : ""})`}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 p-3">
                {!isSingleDay ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <TrendingUp className="h-16 w-16 mb-4 opacity-20" />
                    <p className="font-medium">Gráfico de Ventas por Hora</p>
                    <p className="text-sm mt-1">
                      Disponible solo para rangos de un día
                    </p>
                    <p className="text-xs mt-2">
                      Selecciona un solo día para ver ventas por hora
                    </p>
                  </div>
                ) : salesByHour.filter((h) => h.ingresos > 0).length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <TrendingUp className="h-16 w-16 mb-4 opacity-20" />
                    <p className="font-medium">Sin ventas en este día</p>
                    <p className="text-sm mt-1">
                      Realiza ventas para ver el gráfico
                    </p>
                  </div>
                ) : (
                  <div className="h-full min-h-[300px]">
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                      minWidth={0}
                      minHeight={300}
                    >
                      <LineChart data={salesByHour}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" />
                        <YAxis />
                        <Tooltip
                          formatter={(value) => [
                            `${getCurrencySymbol()}${Number(value).toFixed(2)}`,
                            "Ingresos",
                          ]}
                          labelFormatter={(label) => `Método: ${label}`}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="ingresos"
                          stroke="#3B82F6"
                          strokeWidth={2}
                          name={`Ingresos (${getShopOwnerCurrencySync()})`}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gráfico de métodos de pago */}
            <Card
              className={`h-[400px] flex flex-col ${!can.useReports() ? "opacity-50" : ""}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <PieChartIcon className="h-5 w-5" />
                    Distribución por Método de Pago
                  </CardTitle>
                  {!can.useReports() && (
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                      ⚠️ Requiere Plan Profesional
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 p-3">
                {!can.useReports() ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <BarChart3Icon className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="font-medium text-gray-700 mb-2">
                      Reportes Bloqueados
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Los reportes y análisis están disponibles en los planes
                      Profesional y Enterprise.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => (window.location.href = "/owner/plans")}
                      className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                    >
                      Mejorar Plan
                    </Button>
                  </div>
                ) : (
                  // Contenido original del gráfico
                  <div className="h-full flex flex-col">
                    <div className="flex-1 min-h-0">
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                        minWidth={0}
                        minHeight={200}
                      >
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => [
                              `${getCurrencySymbol()}${Number(value).toFixed(2)}`,
                              "Ingresos",
                            ]}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* {esta sección muestra tarjetas de resumen con 3 columnas para incluir método mixto} */}
                    <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t">
                      {/* Efectivo */}
                      <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
                        <div className="text-xs font-medium text-green-800 mb-1">
                          Efectivo
                        </div>
                        <div className="text-lg font-bold text-green-600">
                          {getCurrencySymbol()}
                          {Number(
                            stats.paymentMethods.efectivo || 0,
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </div>
                        <div className="text-[10px] text-green-600 mt-1">
                          {stats.paymentMethods.efectivo > 0
                            ? `${Math.round((stats.paymentMethods.efectivo / (stats.paymentMethods.efectivo + stats.paymentMethods.transferencia + (stats.paymentMethods.mixto || 0))) * 100)}%`
                            : "0%"}
                        </div>
                      </div>

                      {/* Transferencia */}
                      <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-blue-800 mb-1">
                          Transferencia
                        </div>
                        <div className="text-lg font-bold text-blue-600">
                          {getCurrencySymbol()}
                          {Number(
                            stats.paymentMethods.transferencia || 0,
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </div>
                        <div className="text-[10px] text-blue-600 mt-1">
                          {stats.paymentMethods.transferencia > 0
                            ? `${Math.round((stats.paymentMethods.transferencia / (stats.paymentMethods.efectivo + stats.paymentMethods.transferencia + (stats.paymentMethods.mixto || 0))) * 100)}%`
                            : "0%"}
                        </div>
                      </div>

                      {/* {esta sección agrega tarjeta para método mixto con color púrpura distintivo} */}
                      <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-100">
                        <div className="text-xs font-medium text-purple-800 mb-1">
                          Mixto
                        </div>
                        <div className="text-lg font-bold text-purple-600">
                          {getCurrencySymbol()}
                          {Number(
                            stats.paymentMethods.mixto || 0,
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </div>
                        <div className="text-[10px] text-purple-600 mt-1">
                          {stats.paymentMethods.efectivo +
                            stats.paymentMethods.transferencia +
                            (stats.paymentMethods.mixto || 0) >
                          0
                            ? `${Math.round(((stats.paymentMethods.mixto || 0) / (stats.paymentMethods.efectivo + stats.paymentMethods.transferencia + (stats.paymentMethods.mixto || 0))) * 100)}%`
                            : "0%"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* SEGUNDA FILA DE GRÁFICOS - REORGANIZADA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Top 5 productos más vendidos */}
            <Card
              className={`h-[400px] flex flex-col ${!can.useReports() ? "opacity-50" : ""}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3Icon className="h-5 w-5" />
                    Top 5 Productos Más Vendidos
                  </CardTitle>
                  {!can.useReports() && (
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                      ⚠️ Requiere Plan Profesional
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 p-3">
                {!can.useReports() ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <BarChart3Icon className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="font-medium text-gray-700 mb-2">
                      Análisis de Productos Bloqueado
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      El análisis detallado de productos está disponible en los
                      planes Profesional y Enterprise.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => (window.location.href = "/owner/plans")}
                      className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                    >
                      Mejorar Plan
                    </Button>
                  </div>
                ) : topProducts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <BarChart3Icon className="h-16 w-16 mb-4 opacity-20" />
                    <p className="font-medium">No hay productos vendidos</p>
                    <p className="text-sm mt-1">
                      Los productos aparecerán aquí después de ventas
                    </p>
                  </div>
                ) : (
                  <div className="h-full min-h-[300px]">
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                      minWidth={0}
                      minHeight={300}
                    >
                      <BarChart data={topProducts}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip
                          formatter={(value: number, name: string) => {
                            if (name === "Unidades Vendidas") {
                              return [value, "Unidades"];
                            }
                            return [
                              `${getCurrencySymbol()}${Number(value).toLocaleString()}`,
                              "Ingresos",
                            ];
                          }}
                        />
                        <Legend />
                        <Bar
                          yAxisId="left"
                          dataKey="cantidad"
                          name="Unidades Vendidas"
                          fill="#10B981"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          yAxisId="right"
                          dataKey="ingresos"
                          name={`Ingresos (${getShopOwnerCurrencySync()})`}
                          fill="#3B82F6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* {esta sección mueve Alertas de Inventario aquí, justo después de Top Productos} */}
            <Card>
              <CardHeader>
                <CardTitle>Alertas de Inventario</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Lista de alertas con scroll fijo y paginación */}
                <div className="h-[320px] overflow-y-auto pr-2 space-y-4">
                  {lowStockItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <Package className="h-8 w-8 mb-2 opacity-20" />
                      <p className="text-sm">Todo en orden</p>
                    </div>
                  ) : (
                    paginatedAlerts.map((item) => {
                      const product = products.find(
                        (p) => p.id === item.productId,
                      );
                      return (
                        <div
                          key={item.id}
                          className="flex items-center gap-4 p-3 bg-destructive/10 rounded-lg border border-destructive/20"
                        >
                          <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-bold text-sm text-gray-900 truncate">
                              {product?.name || `Producto ${item.productId}`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Quedan {item.stock} unidades. Umbral:{" "}
                              {item.lowStockThreshold}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Controles de paginación */}
                {alertsTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <span className="text-xs text-muted-foreground">
                      Página {alertsPage} de {alertsTotalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAlertsPage((p) => Math.max(1, p - 1))}
                        disabled={alertsPage === 1}
                        className="h-8 px-3 text-xs"
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setAlertsPage((p) =>
                            Math.min(alertsTotalPages, p + 1),
                          )
                        }
                        disabled={alertsPage === alertsTotalPages}
                        className="h-8 px-3 text-xs"
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* TERCERA FILA - Ventas y Clientes (ORDEN INVERTIDO) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* === ÚLTIMAS 10 VENTAS (AHORA PRIMERO - IZQUIERDA) === */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" /> Últimas 10 Ventas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Lista de ventas con scroll fijo y paginación */}
                <div className="h-[320px] overflow-y-auto pr-2 space-y-4">
                  {stats.recentSales.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <Clock className="h-8 w-8 mb-2 opacity-20" />
                      <p className="text-sm">No hay ventas recientes.</p>
                    </div>
                  ) : (
                    paginatedSales.map((sale) => (
                      <div
                        key={sale.saleId}
                        className="flex justify-between items-center border-b pb-3 last:border-0"
                      >
                        <div>
                          <p
                            className="font-medium text-sm truncate"
                            title={sale.productName}
                          >
                            {(() => {
                              const firstName =
                                sale.productName?.split(" ")[0] ||
                                sale.productName;
                              const count = sale.productCount || 1;

                              return (
                                <>
                                  <span className="sm:hidden">
                                    {firstName}
                                    {count > 1 && (
                                      <span className="text-muted-foreground ml-1">
                                        (+{count - 1})
                                      </span>
                                    )}
                                  </span>
                                  <span className="hidden sm:inline">
                                    {(() => {
                                      const baseName = sale.productName
                                        ? sale.productName
                                        : sale.paymentMethod
                                          ? `Venta por ${sale.paymentMethod}`
                                          : "Venta registrada";
                                      const count = sale.productCount || 1;

                                      return (
                                        <>
                                          {baseName}
                                          {count > 1 && (
                                            <span className="text-muted-foreground ml-1 text-xs font-medium">
                                              (+{count})
                                            </span>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </span>
                                </>
                              );
                            })()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTimeLocal(
                              sale.createdAt instanceof Date
                                ? sale.createdAt.toISOString()
                                : sale.createdAt,
                            )}
                          </p>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs mt-1 ${
                              sale.paymentMethod === "efectivo"
                                ? "bg-green-100 text-green-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {sale.paymentMethod === "efectivo" ? "💵" : "🏦"}{" "}
                            {sale.paymentMethod}
                          </span>
                        </div>
                        <span className="font-bold font-mono text-primary">
                          {getCurrencySymbol()}
                          {Number(sale.total).toFixed(2)}
                        </span>
                      </div>
                    ))
                  )}
                </div>

                {/* Controles de paginación */}
                {salesTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <span className="text-xs text-muted-foreground">
                      Página {salesPage} de {salesTotalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSalesPage((p) => Math.max(1, p - 1))}
                        disabled={salesPage === 1}
                        className="h-8 px-3 text-xs"
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setSalesPage((p) => Math.min(salesTotalPages, p + 1))
                        }
                        disabled={salesPage === salesTotalPages}
                        className="h-8 px-3 text-xs"
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* === ÚLTIMOS 5 CLIENTES (AHORA DESPUÉS - DERECHA) === */}
            <Card
              className={`h-[400px] flex flex-col ${!can.useCustomers() ? "opacity-50" : ""}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" /> Últimos 5 Clientes
                  </CardTitle>
                  {!can.useCustomers() && (
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                      ⚠️ Requiere Plan Profesional
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                {!can.useCustomers() ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Users className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="font-medium text-gray-700 mb-2">
                      Módulo de Clientes Bloqueado
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      El módulo de clientes está disponible en los planes
                      Profesional y Enterprise.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => (window.location.href = "/owner/plans")}
                      className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                    >
                      Mejorar Plan
                    </Button>
                  </div>
                ) : recentCustomers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <Users className="h-12 w-12 mb-4 opacity-20" />
                    <p className="text-center">No hay clientes registrados</p>
                    <p className="text-sm text-center mt-1">
                      Los clientes aparecerán aquí después de su primera compra
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1 min-w-0 mr-3">
                          <div
                            className="font-bold text-sm truncate"
                            title={customer.fullName}
                          >
                            {customer.fullName}
                          </div>
                          <div
                            className="text-xs text-muted-foreground truncate"
                            title={customer.phone}
                          >
                            📱 {customer.phone}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 truncate">
                            {customer.lastPurchaseAt
                              ? `🕒 ${formatDateTimeLocal(customer.lastPurchaseAt)}`
                              : "🕒 Sin compras registradas"}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div
                            className="font-bold text-green-600 text-sm"
                            title={`${getCurrencySymbol()}${customer.totalSpent.toFixed(2)}`}
                          >
                            {getCurrencySymbol()}
                            {customer.totalSpent.toFixed(0)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {customer.totalPurchases} compra
                            {customer.totalPurchases !== 1 ? "s" : ""}
                          </div>
                          <div
                            className={`text-xs font-medium mt-1 ${
                              customer.daysSinceLastPurchase <= 7
                                ? "text-green-600"
                                : customer.daysSinceLastPurchase <= 30
                                  ? "text-amber-600"
                                  : "text-red-600"
                            }`}
                          >
                            {customer.daysSinceLastPurchase <= 7
                              ? "🟢 Activo"
                              : customer.daysSinceLastPurchase <= 30
                                ? "🟡 Regular"
                                : "🔴 Inactivo"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </PageContainer>
  );
}
